import { useCallback, useEffect, useState } from 'react';
import { PhoneCall, Loader2, PlayCircle, AlertTriangle, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { formatDateTime } from '@/lib/dateFormat';
import { toast } from '@/components/ui/toast';
import api from '@/lib/api';
import { SelectField } from '@/components/ui/SelectField';

interface VoiceAgent { id: string; name: string; isActive: boolean }
interface VoiceCall {
  id: string; status: string; toNumber: string; durationSec?: number | null;
  summary?: string | null; outcome?: string | null; recordingUrl?: string | null;
  transcript?: unknown; error?: string | null; createdAt: string; isTest?: boolean;
  agent?: { id: string; name: string } | null;
}
interface VoiceStatus { enabled: boolean; provider: string; configured: boolean; testMode?: boolean; testModeNumber?: string | null }

const STATUS_STYLE: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-600', ringing: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-emerald-100 text-emerald-700',
  no_answer: 'bg-slate-100 text-slate-500', busy: 'bg-slate-100 text-slate-500',
  failed: 'bg-red-100 text-red-700', canceled: 'bg-slate-100 text-slate-500',
};
const fmtDur = (s?: number | null) => {
  if (!s || s < 0) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};

export function LeadCallsPanel({ leadId, consentGiven }: { leadId: string; consentGiven?: boolean }) {
  const [status, setStatus] = useState<VoiceStatus | null>(null);
  const [agents, setAgents] = useState<VoiceAgent[]>([]);
  const [calls, setCalls] = useState<VoiceCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [agentId, setAgentId] = useState('');
  const [placing, setPlacing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [consent, setConsent] = useState(!!consentGiven);
  const [markingConsent, setMarkingConsent] = useState(false);

  useEffect(() => { setConsent(!!consentGiven); }, [consentGiven]);

  const markConsent = async () => {
    setMarkingConsent(true);
    try {
      await api.post(`/leads/${leadId}/gdpr/consent`);
      setConsent(true);
      toast('success', 'Consent recorded');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to record consent');
    } finally {
      setMarkingConsent(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, c] = await Promise.all([
        api.get('/voice/status'),
        api.get('/voice/agents'),
        api.get('/voice/calls', { params: { leadId, limit: 50 } }),
      ]);
      setStatus(s.data);
      setAgents((a.data || []).filter((x: VoiceAgent) => x.isActive));
      setCalls(c.data || []);
      if (!agentId && a.data?.[0]) setAgentId(a.data[0].id);
    } catch {
      /* empty state renders */
    } finally {
      setLoading(false);
    }
  }, [leadId, agentId]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [leadId]);

  const call = async () => {
    if (!agentId) { toast('error', 'Select an agent first'); return; }
    setPlacing(true);
    try {
      await api.post('/voice/calls', { agentId, leadId });
      toast('success', 'AI call placed');
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Call could not be placed');
    } finally {
      setPlacing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>;
  }

  const disabled = !status?.enabled || !status?.configured || agents.length === 0;

  return (
    <div className="space-y-5">
      {/* Call composer */}
      <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center"><PhoneCall className="h-4 w-4 text-[#6366F1]" /></div>
          <h3 className="text-sm font-semibold text-slate-800">Call this lead with AI</h3>
        </div>

        {!status?.enabled && (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> AI Agent Calls are OFF — turn on the master switch in AI Assistant → AI Settings.
          </div>
        )}
        {status?.enabled && !status?.configured && (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Provider not configured — add the Retell key and from-number.
          </div>
        )}
        {agents.length === 0 && (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> No active agent — create one in AI Assistant → Voice Agents.
          </div>
        )}
        {consent ? (
          <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Consent recorded — this lead can be called.
          </div>
        ) : (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> No consent recorded — the call will be blocked while "Require consent" is on.</span>
            <button onClick={markConsent} disabled={markingConsent} className="rounded-md bg-amber-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-amber-700 disabled:opacity-60 whitespace-nowrap">
              {markingConsent ? '…' : 'Mark consent given'}
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <SelectField
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40"
          >
            <option value="">— Select agent —</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </SelectField>
          <button
            onClick={call}
            disabled={placing || disabled}
            className="inline-flex items-center gap-2 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5457e5] disabled:opacity-50"
          >
            {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />} Call now
          </button>
        </div>
        {status?.testMode && (
          <p className="mt-2 text-[11px] text-violet-700 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
            Test Mode is on: this call will ring the test number{status.testModeNumber ? ` ${status.testModeNumber}` : ''} instead of the lead. Turn it off in AI Assistant → AI Settings when you are ready for real calls.
          </p>
        )}
        <p className="mt-2 text-[11px] text-slate-400">⚖️ Only call leads who have given consent. Calls may be skipped or deferred if the master switch is off or outside the lead&apos;s calling hours.</p>
      </div>

      {/* Call history — wrapped in its own card so it stays inside the
          tab's visual boundary (matches the composer card above). */}
      <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Call history</h4>
        {calls.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No AI calls for this lead yet.</p>
        ) : (
          <div className="space-y-2">
            {calls.map((c) => {
              const open = openId === c.id;
              return (
                <div key={c.id} className="rounded-lg border border-gray-100 bg-white">
                  <button onClick={() => setOpenId(open ? null : c.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                    {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    <span className="text-sm text-slate-600 flex-1">{formatDateTime(c.createdAt)}</span>
                    <span className="text-xs text-slate-500">{c.agent?.name ?? '—'}</span>
                    <span className="text-xs text-slate-500 tabular-nums">{fmtDur(c.durationSec)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[c.status] ?? 'bg-slate-100 text-slate-600'}`}>{c.status.replace('_', ' ')}</span>
                    {c.isTest && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-700">Test</span>}
                  </button>
                  {open && (
                    <div className="border-t border-gray-50 px-4 py-3 space-y-3 text-sm">
                      {c.outcome && <div><span className="text-xs font-medium text-slate-400">Outcome: </span><span className="capitalize text-slate-700">{c.outcome}</span></div>}
                      {c.recordingUrl && <audio controls src={c.recordingUrl} className="w-full" />}
                      {c.summary && <div><div className="text-xs font-medium text-slate-400 mb-1">Summary</div><p className="text-slate-700 bg-slate-50 rounded-lg p-3">{c.summary}</p></div>}
                      {c.error && <div><div className="text-xs font-medium text-red-400 mb-1">Error</div><p className="text-red-600 bg-red-50 rounded-lg p-3">{c.error}</p></div>}
                      {!!c.transcript && (
                        <div>
                          <div className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" /> Transcript</div>
                          <pre className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap max-h-64 overflow-auto">{typeof c.transcript === 'string' ? c.transcript : JSON.stringify(c.transcript, null, 2)}</pre>
                        </div>
                      )}
                      {!c.summary && !c.recordingUrl && !c.transcript && !c.error && (
                        <p className="text-xs text-slate-400">The transcript and summary will appear here after the call.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
